/**
 * MergeAccountDialog Component
 * Confirmation dialog for merging two accounts.
 * Transfers all child records from source to target, then soft-deletes the source.
 */
import { useState, useEffect, useMemo } from 'react';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { formatCurrency, formatAccountMask } from '@/utils/format';
import { useMergeAccounts, useAccounts } from '@/hooks/useAccounts';
import { ApiClientError } from '@/services/api';
import { toast } from 'sonner';
import type { AccountWithConnection } from '@/services/accounts';

export interface MergeAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The account to merge FROM (will be soft-deleted) */
  sourceAccount: AccountWithConnection | null;
  /**
   * The account to merge INTO, preselected and always offered even when the
   * dialog's own list (active, visible accounts) does not contain it - e.g.
   * the closed or hidden counterpart of a reported duplicate (Codex review on
   * PR #11). The user can still pick another target.
   */
  suggestedTarget?: AccountWithConnection | null;
}

export default function MergeAccountDialog({
  isOpen,
  onClose,
  sourceAccount,
  suggestedTarget = null,
}: MergeAccountDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const mergeMutation = useMergeAccounts();
  const { data: allAccounts = [] } = useAccounts();

  const suggestedTargetId =
    suggestedTarget && sourceAccount && suggestedTarget.id !== sourceAccount.id
      ? suggestedTarget.id
      : '';

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSelectedTargetId(suggestedTargetId);
    }
  }, [isOpen, suggestedTargetId]);

  // Filter eligible target accounts: same tenant, different account, not deleted, same type
  const eligibleTargets = useMemo(() => {
    if (!sourceAccount) return [];
    const listed = allAccounts.filter(
      (acc) =>
        acc.id !== sourceAccount.id &&
        acc.id !== suggestedTargetId &&
        !acc.isHidden &&
        acc.type === sourceAccount.type
    );
    return suggestedTarget && suggestedTargetId ? [suggestedTarget, ...listed] : listed;
  }, [allAccounts, sourceAccount, suggestedTarget, suggestedTargetId]);

  const selectedTarget = eligibleTargets.find((a) => a.id === selectedTargetId) ?? null;

  const handleClose = () => {
    if (mergeMutation.isPending) return;
    setError(null);
    setSelectedTargetId('');
    onClose();
  };

  const handleConfirm = () => {
    if (!sourceAccount || !selectedTargetId) return;

    setError(null);

    mergeMutation.mutate(
      {
        sourceAccountId: sourceAccount.id,
        targetAccountId: selectedTargetId,
      },
      {
        onSuccess: (result) => {
          const transferred =
            result.transactionsTransferred +
            result.balanceAnchorsTransferred +
            result.statementsTransferred +
            result.holdingsTransferred;
          toast.success(
            `Accounts merged successfully. ${transferred} record${transferred !== 1 ? 's' : ''} transferred.`
          );
          handleClose();
        },
        onError: (err) => {
          if (err instanceof ApiClientError) {
            setError(err.detail);
          } else {
            setError('Failed to merge accounts. Please try again.');
          }
        },
      }
    );
  };

  if (!isOpen || !sourceAccount) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="merge-account-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
        {/* Merge icon */}
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 mb-4">
          <MergeIcon className="h-6 w-6 text-blue-600" />
        </div>

        <h2
          id="merge-account-modal-title"
          className="text-lg font-semibold text-gray-900 text-center mb-2"
        >
          Merge Account
        </h2>

        <p className="text-sm text-gray-600 text-center mb-4">
          Merge{' '}
          <span className="font-medium text-gray-900">
            {sourceAccount.name}
            {sourceAccount.mask ? ` (${formatAccountMask(sourceAccount.mask)})` : ''}
          </span>{' '}
          into another account. All transactions, balance history, and statements will be
          transferred.
        </p>

        {/* Source account info */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Merging from
          </p>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-900">
              {sourceAccount.name}
              {sourceAccount.mask ? ` (${formatAccountMask(sourceAccount.mask)})` : ''}
            </span>
            <span className="text-sm text-gray-600">
              {formatCurrency(sourceAccount.balance, sourceAccount.currency)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {sourceAccount.sourceType === 'manual'
              ? 'Manual account'
              : sourceAccount.connection?.institutionName ?? 'Connected account'}
          </p>
        </div>

        {/* Target account selector */}
        <div className="mb-4">
          <label
            htmlFor="merge-target-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Merge into
          </label>
          {eligibleTargets.length === 0 ? (
            <Alert variant="warning">
              No compatible accounts found. Accounts must be the same type to merge.
            </Alert>
          ) : (
            <select
              id="merge-target-select"
              value={selectedTargetId}
              onChange={(e) => setSelectedTargetId(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              disabled={mergeMutation.isPending}
            >
              <option value="">Select an account...</option>
              {eligibleTargets.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name}
                  {acc.mask ? ` (${formatAccountMask(acc.mask)})` : ''}
                  {' - '}
                  {formatCurrency(acc.balance, acc.currency)}
                  {acc.sourceType !== 'manual'
                    ? ` - ${acc.connection?.institutionName ?? 'Connected'}`
                    : ' - Manual'}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Selected target info */}
        {selectedTarget && (
          <div className="bg-blue-50 rounded-lg p-3 mb-4">
            <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">
              Merging into
            </p>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-900">
                {selectedTarget.name}
                {selectedTarget.mask
                  ? ` (${formatAccountMask(selectedTarget.mask)})`
                  : ''}
              </span>
              <span className="text-sm text-gray-600">
                {formatCurrency(selectedTarget.balance, selectedTarget.currency)}
              </span>
            </div>
            <p className="text-xs text-blue-700 mt-0.5">
              {selectedTarget.sourceType === 'manual'
                ? 'Manual account'
                : selectedTarget.connection?.institutionName ?? 'Connected account'}
            </p>
          </div>
        )}

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 mb-4">
          <p className="text-sm text-yellow-800">
            This action cannot be undone. The source account will be removed and all its
            records will be moved to the target account.
          </p>
        </div>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mergeMutation.isPending}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleConfirm}
            isLoading={mergeMutation.isPending}
            disabled={!selectedTargetId || eligibleTargets.length === 0}
            className="flex-1"
          >
            Merge Accounts
          </Button>
        </div>
      </div>
    </div>
  );
}

function MergeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
      />
    </svg>
  );
}
